import { useNavigate } from 'react-router-dom';
import T from '../theme';
import Icon from '../components/Icon';
import Reveal from '../components/Reveal';
import { PageHero, SectionWrap, FooterCTA } from '../components/shared';
import { PROCESS_STEPS } from '../data';

const F = "'Plus Jakarta Sans', sans-serif";
const MONO = "'DM Mono', monospace";
const STEP_ICONS = [
  'phone_in_talk',
  'description',
  'manage_search',
  'draw',
  'rocket_launch',
];

export default function Process() {
  return (
    <div style={{ background: T.bg }}>
      <style>{`
        .process-section {
          padding-top: 72px;
        }

        .process-timeline {
          max-width: 740px;
          margin: 0 auto;
        }

        .process-step {
          display: grid;
          grid-template-columns: 84px 1fr;
          gap: 24px;
          padding-bottom: 68px;
          position: relative;
        }

        .process-step-icon-column {
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .process-step-icon {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          border: 2px solid ${T.aqua};
          display: flex;
          align-items: center;
          justify-content: center;
          background: ${T.aquaGhost};
          box-shadow: 0 0 0 6px ${T.aquaGhost2};
          flex-shrink: 0;
        }

        .process-step-line {
          position: absolute;
          left: 42px;
          top: 84px;
          bottom: 0;
          width: 1px;
          background: linear-gradient(
            to bottom,
            ${T.aqua},
            transparent
          );
          opacity: 0.3;
        }

        .process-step-content {
          padding-top: 14px;
        }

        .process-step-heading {
          display: flex;
          align-items: center;
          gap: 14px;
          margin-bottom: 12px;
          flex-wrap: wrap;
        }

        .process-step-title {
          font-family: ${F};
          font-weight: 700;
          font-size: 1.3rem;
          color: ${T.ink};
          margin: 0;
        }

        .process-step-duration {
          font-family: ${MONO};
          font-size: 0.68rem;
          color: ${T.aquaDark};
          background: ${T.aquaGhost};
          border: 1px solid ${T.border};
          border-radius: 4px;
          padding: 3px 10px;
          white-space: nowrap;
        }

        .process-step-description {
          font-size: 1rem;
          color: ${T.muted};
          line-height: 1.85;
          margin: 0;
        }

        .process-values {
          margin-top: 48px;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .process-value-card {
          padding: 28px 24px;
          background: ${T.surface};
          border: 1.5px solid ${T.borderGray};
          border-radius: 12px;
          box-shadow: 0 1px 4px rgba(11,31,58,0.05);
        }

        .process-value-title {
          font-family: ${F};
          font-weight: 700;
          font-size: 1.05rem;
          margin: 0 0 8px;
          color: ${T.ink};
        }

        .process-value-description {
          font-size: 0.92rem;
          color: ${T.muted};
          line-height: 1.75;
          margin: 0;
        }

        @media (max-width: 900px) {
          .process-values {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 600px) {
          .process-section {
            padding-top: 48px;
          }

          .process-timeline {
            max-width: 100%;
          }

          .process-step {
            grid-template-columns: 54px 1fr;
            gap: 16px;
            padding-bottom: 48px;
          }

          .process-step-icon {
            width: 46px;
            height: 46px;
            box-shadow: 0 0 0 5px ${T.aquaGhost2};
          }

          .process-step-line {
            left: 26px;
            top: 66px;
          }

          .process-step-content {
            padding-top: 3px;
          }

          .process-step-heading {
            gap: 8px;
            margin-bottom: 9px;
          }

          .process-step-title {
            font-size: 1.1rem;
            line-height: 1.35;
          }

          .process-step-duration {
            font-size: 0.62rem;
            padding: 3px 8px;
          }

          .process-step-description {
            font-size: 0.94rem;
            line-height: 1.7;
          }

          .process-values {
            margin-top: 20px;
            grid-template-columns: 1fr;
            gap: 14px;
          }

          .process-value-card {
            padding: 22px 20px;
          }

          .process-value-title {
            font-size: 1rem;
          }

          .process-value-description {
            font-size: 0.9rem;
            line-height: 1.7;
          }
        }

        @media (max-width: 380px) {
          .process-step {
            grid-template-columns: 46px 1fr;
            gap: 13px;
          }

          .process-step-icon {
            width: 40px;
            height: 40px;
          }

          .process-step-line {
            left: 23px;
            top: 59px;
          }

          .process-step-title {
            font-size: 1.02rem;
          }

          .process-step-description {
            font-size: 0.9rem;
          }
        }
      `}</style>

      <PageHero
        
        title={
          <>
            Transparent from day one.
            <br />
            <span style={{ color: T.aquaDark }}>
              No black-box delivery.
            </span>
          </>
        }
        sub="Every engagement follows the same clear path. You always know what's happening, why, and what comes next."
      />

      <SectionWrap className="process-section">
        <div className="process-timeline">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal
              key={step.number}
              variant="up"
              delay={`${i * 0.1}s`}
            >
              <div className="process-step">
                {i < PROCESS_STEPS.length - 1 && (
                  <div className="process-step-line" />
                )}

                <div className="process-step-icon-column">
                  <div className="process-step-icon">
                    <Icon
                      name={STEP_ICONS[i]}
                      size={24}
                      color={T.aquaDark}
                      fill={1}
                    />
                  </div>
                </div>

                <div className="process-step-content">
                  <div className="process-step-heading">
                    <h3 className="process-step-title">
                      {step.title}
                    </h3>

                    <span className="process-step-duration">
                      {step.duration}
                    </span>
                  </div>

                  <p className="process-step-description">
                    {step.desc}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Value props */}
        <div className="process-values">
          {[
            {
              icon: 'price_check',
              title: 'Fixed pricing',
              desc: 'Every project has a defined price agreed upfront. No hourly surprises.',
            },
            {
              icon: 'speed',
              title: 'Fast turnaround',
              desc: 'Most projects finish in 1–3 weeks. We move at small-team speed.',
            },
            {
              icon: 'task_alt',
              title: 'Real deliverables',
              desc: "Reports you can act on, designs you can ship, code that's already built.",
            },
          ].map((v, i) => (
            <Reveal
              key={i}
              variant="up"
              delay={`${i * 0.1}s`}
            >
              <div className="process-value-card">
                <Icon
                  name={v.icon}
                  size={24}
                  color={T.aqua}
                  fill={1}
                  style={{ marginBottom: 12 }}
                />

                <h4 className="process-value-title">
                  {v.title}
                </h4>

                <p className="process-value-description">
                  {v.desc}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </SectionWrap>

      <FooterCTA />
    </div>
  );
}